<script setup lang="ts">
import type { DictionaryItem } from '@/store/modules/dictionary'
import { useDictionaryStore } from '@/store/modules/dictionary'

defineOptions({
  name: 'AppDictRadio',
})

const props = withDefaults(defineProps<{
  dictName?: string
  data?: DictionaryItem[] | (() => DictionaryItem[])
  button?: boolean
}>(), {
  dictName: '',
  data: () => [],
  button: false,
})

const model = defineModel<any>()
const attrs = useAttrs()
const dictionaryStore = useDictionaryStore()
const options = ref<DictionaryItem[]>([])
const BOOLEAN_TRUE_VALUE = '__app_dict_boolean_true__'
const BOOLEAN_FALSE_VALUE = '__app_dict_boolean_false__'

function normalizeValueByModelType(value: any) {
  if (typeof model.value === 'number') {
    const normalized = Number(value)
    return Number.isNaN(normalized) ? value : normalized
  }
  if (typeof model.value === 'boolean') {
    if (typeof value === 'string') {
      return ['1', 'true', 'yes', 'on'].includes(value.toLowerCase())
    }
    return Boolean(value)
  }
  return value
}

const normalizedModelValue = computed({
  get: () => {
    const value = normalizeValueByModelType(model.value)
    if (typeof model.value === 'boolean') {
      return value ? BOOLEAN_TRUE_VALUE : BOOLEAN_FALSE_VALUE
    }
    return value
  },
  set: (value) => {
    if (typeof model.value === 'boolean') {
      model.value = value === BOOLEAN_TRUE_VALUE
      return
    }
    model.value = normalizeValueByModelType(value)
  },
})

const normalizedOptions = computed(() => {
  return options.value.map(item => ({
    label: item.name,
    value: typeof model.value === 'boolean'
      ? (normalizeValueByModelType(item.value) ? BOOLEAN_TRUE_VALUE : BOOLEAN_FALSE_VALUE)
      : normalizeValueByModelType(item.value),
  }))
})

async function loadOptions() {
  if (props.dictName) {
    options.value = await dictionaryStore.get(props.dictName)
    return
  }
  options.value = typeof props.data === 'function' ? props.data() : props.data
}

watch(
  () => [props.dictName, props.data] as const,
  () => {
    void loadOptions()
  },
  { immediate: true },
)
</script>

<template>
  <FaRadioGroup
    v-bind="attrs"
    v-model="normalizedModelValue"
    :options="normalizedOptions"
    :class="props.button ? 'inline-flex gap-0 overflow-hidden rounded-md border border-input bg-background shadow-xs' : 'flex flex-wrap gap-4'"
    :option-class="props.button ? 'gap-0 border-e border-input last:border-e-0' : undefined"
  >
    <template v-if="props.button" #option="{ option, checked }">
      <span
        class="text-sm font-medium px-4 flex h-9 min-w-14 items-center justify-center"
        :class="checked ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground'"
      >
        {{ option.label }}
      </span>
    </template>
  </FaRadioGroup>
</template>
